import React, { useCallback, useEffect, useState } from 'react';
import { useLatest } from 'react-use';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { getDataSource } from '../components/utils';
import apptracker from '@utils/sensor/apptracker';
import {
  OPEN_FILTER_MANAGEMENT_PANEL,
  CREATE_FILTER_ON_FILTER_MANAGEMENT,
  DELETE_FILTER_ON_FILTER_MANAGEMENT,
} from '@src/utils/sensor/track';
import { Management, DataSource, type ManagementProps } from '../components';
import {
  useFilterMeta,
  useGetSavedFilters,
  useDeleteFilter,
  useCreateFilter,
  useEditFilter,
  useEnableFilter,
  useNotifyFilterPopup,
  useDisabledDataSources,
  useHandleEditFilterInitialize,
} from './hooks';

export const OPEN_FILTER_MANAGEMENT_EVENT = 'open-filter-management';

/**
 *  ADS and VIDEO share the same saved filters, but after the release of 8th Jan 2025, the filter metadata is separated,
 *  so force to set the default data source to video if the current data source is ads to avoid the inconsistency.
 */
function getDefaultDataSource() {
  const dataSource = getDataSource();
  return dataSource === DataSource.ads ? DataSource.video : dataSource;
}

export const FilterManagement = () => {
  const [dataSource, setDataSource] = useState<DataSource>(getDefaultDataSource);
  const [visible, setVisible] = useState(false);
  const [enableApplyAction, setEnableApplyAction] = useState(false);
  const notifyActions = useLatest(useNotifyFilterPopup());
  const disabledDataSources = useDisabledDataSources();
  const { mutateAsync: deleteFilter } = useDeleteFilter();
  const { mutateAsync: editFilter } = useEditFilter();
  const { mutateAsync: enableFilter } = useEnableFilter();
  const { mutateAsync: createFilter } = useCreateFilter();
  const {
    data: filterMeta,
    isLoading: filterMetaLoading,
    isError: filterMetaError,
    refetch: refetchFilterMeta,
  } = useFilterMeta(dataSource, {
    enabled: visible && !disabledDataSources.includes(dataSource),
  });

  const {
    data: savedFilters,
    isFetching: savedFiltersLoading,
    isError: savedFiltersError,
    refetch: refetchSavedFilters,
  } = useGetSavedFilters(dataSource, {
    enabled: visible && !disabledDataSources.includes(dataSource),
  });

  // logic to handle the filterBuilder for edit filter through event.
  const { setEditFilterId, filterEditor, setFilterEditor } = useHandleEditFilterInitialize({
    savedFilters,
    filterMeta,
  });

  useEffect(() => {
    const handleOpenFilterManagement = (e: Event) => {
      apptracker(OPEN_FILTER_MANAGEMENT_PANEL, {
        product: CURRENT_PRODUCT_MODE,
      });

      if ((e as CustomEvent<{ enableApplyAction?: boolean }>)?.detail?.enableApplyAction) {
        setEnableApplyAction(true);
      }
      if ((e as CustomEvent<{ editFilterId?: number }>)?.detail?.editFilterId) {
        setEditFilterId(e?.detail?.editFilterId);
      }
      setVisible(true);
    };
    window.addEventListener(OPEN_FILTER_MANAGEMENT_EVENT, handleOpenFilterManagement);
    return () => {
      window.removeEventListener(OPEN_FILTER_MANAGEMENT_EVENT, handleOpenFilterManagement);
    };
  }, []);

  React.useEffect(() => {
    if (!visible) {
      setEnableApplyAction(false);
    }
  }, [visible]);

  const handleDataSourceChange = useCallback((d: DataSource) => {
    setDataSource(d);
    handleFilterEditorReset();
  }, []);

  const handleFilterEditorReset = useCallback(() => {
    setFilterEditor(null);
    setEditFilterId(null);
  }, []);

  const handleFilterMetaRetry = useCallback(() => {
    if (filterMetaError) {
      refetchFilterMeta();
    }
  }, [filterMetaError, refetchFilterMeta]);

  const handleSavedFiltersRetry = useCallback(() => {
    if (savedFiltersError) {
      refetchSavedFilters();
    }
  }, [savedFiltersError, refetchSavedFilters]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setDataSource(getDefaultDataSource);
    handleFilterEditorReset();
  }, []);

  const handleDeleteFilter = useCallback<NonNullable<ManagementProps['onDeleteFilter']>>(
    async (ids, d) => {
      const { data, status } = await deleteFilter({
        ids,
        dataSource: d,
      });

      let deletedIds = ids;

      if (status === 207 && data?.success) {
        deletedIds = data.success;
      }

      if (deletedIds.length) {
        notifyActions.current.onDeleteNotify(deletedIds, d);
        refetchSavedFilters();

        apptracker(DELETE_FILTER_ON_FILTER_MANAGEMENT, {
          ids: deletedIds,
          dataSource: d,
          product: CURRENT_PRODUCT_MODE,
        });
      }

      return data;
    },
    [deleteFilter, refetchSavedFilters, notifyActions],
  );

  const handleEditFilter = useCallback<NonNullable<ManagementProps['onEditFilter']>>(
    async (id, filter, d) => {
      const result = await editFilter({
        id,
        filter,
        dataSource: d,
      });
      notifyActions.current.onUpdateNotify(id, filter, d);
      refetchSavedFilters();
      return result.data;
    },
    [editFilter, refetchSavedFilters, notifyActions],
  );

  const handleEnableFilter = useCallback<NonNullable<ManagementProps['onEnableFilter']>>(
    async (id, d) => {
      const result = await enableFilter({
        id,
        dataSource: d,
      });
      refetchSavedFilters();
      return result.data;
    },
    [enableFilter, refetchSavedFilters],
  );

  const handleCreateFilter = useCallback<NonNullable<ManagementProps['onCreateFilter']>>(
    async (filter, d) => {
      const result = await createFilter({
        filter,
        dataSource: d,
      });
      const newSavedFilter = result.data.filter;
      if (newSavedFilter) {
        apptracker(CREATE_FILTER_ON_FILTER_MANAGEMENT, {
          filterId: newSavedFilter.fid,
          dataSource,
          product: CURRENT_PRODUCT_MODE,
        });
        notifyActions.current.onCreateNotify(d, newSavedFilter);
      }
      refetchSavedFilters();
      return result.data;
    },
    [createFilter, refetchSavedFilters, dataSource, notifyActions],
  );

  return (
    <div
      id="filter-management-container"
      data-testid="filter-management"
      data-ifc-event={OPEN_FILTER_MANAGEMENT_EVENT}
    >
      <Management
        isFilterMetaLoading={filterMetaLoading}
        isSavedFiltersLoading={savedFiltersLoading}
        isFilterMetaError={filterMetaError}
        isSavedFiltersError={savedFiltersError}
        disabledDataSources={disabledDataSources}
        visible={visible}
        enableApplyAction={enableApplyAction}
        filterEditor={filterEditor}
        dataSource={dataSource}
        filterMeta={filterMeta!}
        savedFilters={savedFilters || []}
        onDataSourceChange={handleDataSourceChange}
        onFilterEditorReset={handleFilterEditorReset}
        onClose={handleClose}
        onDeleteFilter={handleDeleteFilter}
        onEditFilter={handleEditFilter}
        onEnableFilter={handleEnableFilter}
        onCreateFilter={handleCreateFilter}
        onFilterMetaRetry={handleFilterMetaRetry}
        onSavedFiltersRetry={handleSavedFiltersRetry}
        onApplyNotify={notifyActions.current.onApplyNotify}
      />
    </div>
  );
};
